/**
 * VoiceTuningPanel — Admin "Voice AI Tuning" module (admin-only, READ-ONLY).
 *
 * Operational observability for the AI telephone booking channel: a metrics
 * dashboard (completion rate, transfers, failures, avg length/clarifications,
 * top failure reasons, misunderstood locations/postcodes/landmarks, confidence
 * distribution, recognition rates, daily trend) plus a browsable Learning Log
 * of every conversation with full transcript, AI responses, confidence,
 * geocoder result, final booking and escalation reason. Pure reporting — it
 * never touches dispatch / lifecycle / pricing.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  PhoneCall, CheckCircle2, PhoneForwarded, XCircle, RefreshCw, Gauge,
  MessageSquare, MapPin, Hash, Landmark, TrendingUp, ListFilter, X, Bot,
  User, ArrowRightLeft, Search, Repeat, Languages, Sparkles, RotateCcw,
} from "lucide-react";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const OUTCOME_META = {
  completed: { label: "Booked", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  human_transfer: { label: "Transferred", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border-red-200" },
  in_progress: { label: "In progress", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-1">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
      <Icon className="w-4 h-4" style={{ color: accent }} /> {label}
    </div>
    <div className="text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

const RankList = ({ icon: Icon, title, rows, empty, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
      <Icon className="w-4 h-4 text-slate-500" /> {title}
    </div>
    {(!rows || rows.length === 0) ? (
      <div className="text-xs text-slate-400 py-2">{empty}</div>
    ) : (
      <ul className="space-y-1.5">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-slate-700 truncate" title={r.label || r.text || r.reason}>
              {r.label || r.text || r.reason}
            </span>
            <span className="shrink-0 tabular-nums text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 text-xs">{r.count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ConfidenceBar = ({ dist }) => {
  const total = (dist.high || 0) + (dist.medium || 0) + (dist.low || 0) || 1;
  const seg = [
    { k: "high", c: "#059669", n: dist.high || 0, label: "High" },
    { k: "medium", c: "#d97706", n: dist.medium || 0, label: "Medium" },
    { k: "low", c: "#dc2626", n: dist.low || 0, label: "Low" },
  ];
  return (
    <div data-testid="voice-confidence-dist" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
        <Gauge className="w-4 h-4 text-slate-500" /> Confidence distribution
      </div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        {seg.map((s) => (
          <div key={s.k} style={{ width: `${(100 * s.n) / total}%`, backgroundColor: s.c }} />
        ))}
      </div>
      <div className="mt-3 flex gap-4 text-xs">
        {seg.map((s) => (
          <div key={s.k} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.c }} />
            <span className="text-slate-600">{s.label}</span>
            <span className="font-semibold text-slate-900 tabular-nums">{s.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LanguageBar = ({ langs }) => {
  const rows = langs || [];
  const total = rows.reduce((a, r) => a + (r.count || 0), 0) || 1;
  const palette = ["#0062FF", "#059669", "#d97706", "#7C3AED", "#dc2626"];
  return (
    <div data-testid="voice-language-dist" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
        <Languages className="w-4 h-4 text-slate-500" /> Language distribution
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No calls in this period.</div>
      ) : (
        <>
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
            {rows.map((r, i) => (
              <div key={r.code} style={{ width: `${(100 * (r.count || 0)) / total}%`, backgroundColor: palette[i % palette.length] }} />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            {rows.map((r, i) => (
              <div key={r.code} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: palette[i % palette.length] }} />
                <span className="text-slate-600">{r.label}</span>
                <span className="font-semibold text-slate-900 tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


const TrendChart = ({ trend }) => {
  const max = Math.max(1, ...trend.map((t) => t.total));
  return (
    <div data-testid="voice-trend" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
        <TrendingUp className="w-4 h-4 text-slate-500" /> Daily calls &amp; bookings
      </div>
      {trend.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No calls in this period yet.</div>
      ) : (
        <div className="flex items-end gap-1.5 h-28">
          {trend.map((t) => (
            <div key={t.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${t.date}: ${t.completed}/${t.total} booked`}>
              <div className="w-full rounded-t bg-slate-200 relative flex items-end" style={{ height: `${(100 * t.total) / max}%`, minHeight: 4 }}>
                <div className="w-full rounded-t bg-emerald-500" style={{ height: t.total ? `${(100 * t.completed) / t.total}%` : "0%" }} />
              </div>
              <span className="text-[9px] text-slate-400">{t.date.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Booked</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> Total calls</span>
      </div>
    </div>
  );
};

const CallDetailDrawer = ({ callSid, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/voice/calls/${encodeURIComponent(callSid)}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [callSid]);

  const meta = detail ? (OUTCOME_META[detail.outcome] || OUTCOME_META.in_progress) : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="voice-call-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Conversation</h3>
          <button data-testid="voice-call-detail-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        {loading || !detail ? (
          <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
              <span className="text-slate-500">{fmtDate(detail.created_at)}</span>
              <span className="text-slate-700 font-medium">{detail.from_phone || "Unknown caller"}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-400 font-semibold">Confidence</div>
                <div className="font-semibold text-slate-800">{detail.confidence != null ? detail.confidence : "—"}</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-400 font-semibold">Clarifications</div>
                <div className="font-semibold text-slate-800">{detail.clarifications} · {detail.turns} turns</div>
              </div>
            </div>

            {detail.outcome_reason && detail.outcome !== "completed" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Escalation reason: <span className="font-semibold">{detail.outcome_reason.replace(/_/g, " ")}</span>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Transcript</div>
              <div className="space-y-2">
                {(detail.dialog || []).length === 0 && <div className="text-xs text-slate-400">No dialog captured.</div>}
                {(detail.dialog || []).map((d, i) => (
                  <div key={i} className="space-y-1">
                    {d.ai && (
                      <div className="flex gap-2">
                        <Bot className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <div className="bg-emerald-50 text-slate-800 rounded-lg px-3 py-1.5 text-sm">{d.ai}</div>
                      </div>
                    )}
                    {d.caller && (
                      <div className="flex gap-2 justify-end">
                        <div className="bg-slate-100 text-slate-800 rounded-lg px-3 py-1.5 text-sm">{d.caller}</div>
                        <User className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Spoken → geocoder</div>
                <div className="text-slate-700"><b>Pickup:</b> {detail.pickup_text || "—"}{detail.geocode?.pickup_matched ? ` → ${detail.geocode.pickup_matched}` : ""}</div>
                <div className="text-slate-700"><b>Dropoff:</b> {detail.dropoff_text || "—"}{detail.geocode?.dropoff_matched ? ` → ${detail.geocode.dropoff_matched}` : ""}</div>
              </div>
              {(detail.corrections || []).length > 0 && (
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="text-[11px] uppercase text-slate-400 font-semibold mb-1">AI corrections</div>
                  {detail.corrections.map((c, i) => (
                    <div key={i} className="text-slate-700 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3 text-slate-400" /> {c.from} → {c.to}</div>
                  ))}
                </div>
              )}
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Final booking</div>
                {detail.ride ? (
                  <div className="text-slate-700">Ride {detail.ride.id?.slice(0, 8)} · {detail.ride.status} · £{detail.ride.estimated_fare}</div>
                ) : detail.completed ? (
                  <div className="text-slate-700">Booked (ride {detail.ride_id?.slice(0, 8) || "—"})</div>
                ) : (
                  <div className="text-slate-400">No booking created.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const VoiceTuningPanel = () => {
  const [range, setRange] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [view, setView] = useState("dashboard");
  const [analytics, setAnalytics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [outcome, setOutcome] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [openCall, setOpenCall] = useState(null);

  const rangeParams = useCallback(() => {
    const p = new URLSearchParams({ range });
    if (range === "custom") {
      if (customStart) p.set("start", customStart);
      if (customEnd) p.set("end", customEnd);
    }
    return p;
  }, [range, customStart, customEnd]);

  const loadAnalytics = useCallback(async () => {
    if (range === "custom" && (!customStart || !customEnd)) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/voice/analytics?${rangeParams().toString()}`);
      setAnalytics(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [range, customStart, customEnd, rangeParams]);

  const loadCalls = useCallback(async () => {
    if (range === "custom" && (!customStart || !customEnd)) return;
    setLoading(true);
    try {
      const params = rangeParams();
      params.set("outcome", outcome);
      params.set("limit", "200");
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/voice/calls?${params.toString()}`);
      setCalls(r.data.calls || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [range, customStart, customEnd, outcome, q, rangeParams]);

  useEffect(() => { if (view === "dashboard") loadAnalytics(); }, [view, loadAnalytics]);
  useEffect(() => { if (view === "log") loadCalls(); }, [view, loadCalls]);

  const t = analytics?.totals || {};

  return (
    <div data-testid="voice-tuning-panel" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-600" /> Voice AI Tuning
          </h2>
          <p className="text-sm text-slate-500">Improve the AI from real production calls. Read-only · advisory.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.id}
                data-testid={`voice-range-${r.id}`}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 text-xs font-medium ${range === r.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >{r.label}</button>
            ))}
          </div>
          {range === "custom" && (
            <div className="flex items-center gap-1" data-testid="voice-custom-range">
              <input type="date" data-testid="voice-custom-start" value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2 py-1 text-xs rounded-lg border border-slate-200" />
              <span className="text-slate-400 text-xs">→</span>
              <input type="date" data-testid="voice-custom-end" value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2 py-1 text-xs rounded-lg border border-slate-200" />
            </div>
          )}
          <button
            data-testid="voice-refresh"
            onClick={() => (view === "dashboard" ? loadAnalytics() : loadCalls())}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
          ><RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button
          data-testid="voice-view-dashboard"
          onClick={() => setView("dashboard")}
          className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${view === "dashboard" ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >Dashboard</button>
        <button
          data-testid="voice-view-log"
          onClick={() => setView("log")}
          className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${view === "log" ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >Learning Log</button>
      </div>

      {view === "dashboard" && analytics && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard testid="voice-stat-total" icon={PhoneCall} label="Total AI calls" value={t.total_calls ?? 0} accent="#0062FF" />
            <StatCard testid="voice-stat-completed" icon={CheckCircle2} label="Bookings" value={t.completed ?? 0} sub={`${t.completion_rate ?? 0}% completion`} accent="#059669" />
            <StatCard testid="voice-stat-transfers" icon={PhoneForwarded} label="Human transfers" value={t.human_transfers ?? 0} accent="#d97706" />
            <StatCard testid="voice-stat-failed" icon={XCircle} label="Failed bookings" value={t.failed ?? 0} sub={`${t.escalation_rate ?? 0}% escalation`} accent="#dc2626" />
            <StatCard testid="voice-stat-avglen" icon={MessageSquare} label="Avg conversation" value={`${t.avg_conversation_turns ?? 0}`} sub="caller turns" accent="#0F766E" />
            <StatCard testid="voice-stat-avgclar" icon={ListFilter} label="Avg clarifications" value={`${t.avg_clarifications ?? 0}`} accent="#6D28D9" />
            <StatCard testid="voice-stat-pcrecog" icon={Hash} label="Postcode recognition" value={`${analytics.recognition?.postcode?.rate ?? 0}%`} sub={`${analytics.recognition?.postcode?.ok ?? 0}/${analytics.recognition?.postcode?.used ?? 0}`} accent="#0062FF" />
            <StatCard testid="voice-stat-lmrecog" icon={Landmark} label="Landmark recognition" value={`${analytics.recognition?.landmark?.rate ?? 0}%`} sub={`${analytics.recognition?.landmark?.ok ?? 0}/${analytics.recognition?.landmark?.used ?? 0}`} accent="#059669" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <TrendChart trend={analytics.trend || []} />
            <ConfidenceBar dist={analytics.confidence_distribution || {}} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <RankList testid="voice-rank-reasons" icon={XCircle} title="Top failure / escalation reasons" rows={analytics.top_failure_reasons} empty="No escalations in this period." />
            <RankList testid="voice-rank-corrected" icon={ArrowRightLeft} title="Frequently corrected" rows={analytics.frequently_corrected} empty="No corrections recorded." />
            <RankList testid="voice-rank-locations" icon={MapPin} title="Top misunderstood locations" rows={analytics.top_misunderstood_locations} empty="No misunderstood locations." />
            <RankList testid="voice-rank-postcodes" icon={Hash} title="Top misunderstood postcodes" rows={analytics.top_misunderstood_postcodes} empty="No misunderstood postcodes." />
            <RankList testid="voice-rank-landmarks" icon={Landmark} title="Top misunderstood landmarks" rows={analytics.top_misunderstood_landmarks} empty="No misunderstood landmarks." />
          </div>

          {/* Slice 3 — Corrections & Recovery (first-class amendment events) */}
          <div className="pt-2">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-violet-600" /> Corrections & Recovery
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard testid="voice-stat-recovery" icon={RotateCcw} label="Conversation recovery"
                value={`${analytics.amendments?.conversation_recovery_rate ?? 0}%`}
                sub={`${analytics.amendments?.recovered_conversations ?? 0}/${analytics.amendments?.corrected_conversations ?? 0} recovered`} accent="#7C3AED" />
              <StatCard testid="voice-stat-amendments" icon={ArrowRightLeft} label="Total amendments"
                value={analytics.amendments?.total_amendments ?? 0} accent="#0891B2" />
              <StatCard testid="voice-stat-avgcorr" icon={Repeat} label="Avg corrections / booking"
                value={analytics.amendments?.average_corrections_per_booking ?? 0} accent="#D97706" />
              <StatCard testid="voice-stat-corrected-convos" icon={MessageSquare} label="Corrected conversations"
                value={analytics.amendments?.corrected_conversations ?? 0} accent="#0F766E" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <RankList testid="voice-rank-amend-types" icon={ArrowRightLeft} title="Amendments by type"
                rows={(analytics.amendments?.amendments_by_type || []).map((x) => ({ text: x.label, count: x.count }))}
                empty="No amendments in this period." />
              <RankList testid="voice-rank-corrected-locations" icon={MapPin} title="Most corrected locations"
                rows={analytics.amendments?.most_corrected_locations} empty="No location corrections." />
              <RankList testid="voice-rank-corrected-postcodes" icon={Hash} title="Most corrected postcodes"
                rows={analytics.amendments?.most_corrected_postcodes} empty="No postcode corrections." />
              <LanguageBar langs={analytics.language_distribution || []} />
            </div>
            <div className="mt-3">
              <RankList testid="voice-rank-learning" icon={Sparkles} title="AI learning opportunities (most-corrected calls)"
                rows={(analytics.amendments?.ai_learning_opportunities || []).map((x) => ({ text: x.session_id, count: x.corrections }))}
                empty="No high-correction calls — the AI is getting it right first time." />
            </div>
          </div>
        </div>
      )}

      {view === "log" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              {["all", "completed", "human_transfer", "failed"].map((o) => (
                <button
                  key={o}
                  data-testid={`voice-outcome-${o}`}
                  onClick={() => setOutcome(o)}
                  className={`px-3 py-1.5 text-xs font-medium capitalize ${outcome === o ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >{o === "all" ? "All" : (OUTCOME_META[o]?.label || o)}</button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="voice-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadCalls()}
                placeholder="Search caller / address / speech…"
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
            <button data-testid="voice-search-btn" onClick={loadCalls} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800">Search</button>
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Caller</th>
                  <th className="text-left px-3 py-2 font-semibold">Pickup → Dropoff</th>
                  <th className="text-left px-3 py-2 font-semibold">Outcome</th>
                  <th className="text-right px-3 py-2 font-semibold">Turns</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {calls.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No conversations found.</td></tr>
                )}
                {calls.map((c) => {
                  const meta = OUTCOME_META[c.outcome] || OUTCOME_META.in_progress;
                  return (
                    <tr
                      key={c.call_sid}
                      data-testid={`voice-call-row-${c.call_sid}`}
                      onClick={() => setOpenCall(c.call_sid)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                      <td className="px-3 py-2 text-slate-700">{c.from_phone || "Unknown"}</td>
                      <td className="px-3 py-2 text-slate-700 max-w-[280px] truncate">
                        {(c.pickup_text || "—")} → {(c.dropoff_text || "—")}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                        {c.outcome_reason && c.outcome !== "completed" && (
                          <span className="ml-1 text-[11px] text-slate-400">{c.outcome_reason.replace(/_/g, " ")}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{c.turns}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openCall && <CallDetailDrawer callSid={openCall} onClose={() => setOpenCall(null)} />}
    </div>
  );
};

export default VoiceTuningPanel;
